import { authApi } from '@core/api/httpClient'
import { AUTH_ENDPOINTS } from './authEndpointPaths'
import {
  parseLogoutResponse,
  parseUaePassDisableResponse,
  parseLoginMethodsResponse,
} from './authResponseParser'
import type {
  LoginPayloadDto,
  LoginResponseDto,
  VerifyOtpPayloadDto,
  VerifyOtpResponseDto,
  ResendOtpPayloadDto,
  ResendOtpResponseDto,
  PrivacyPolicyResponseDto,
  TermsAndConditionsResponseDto,
  InvestorRiskPolicyResponseDto,
  UaePassCallbackPayloadDto,
  UaePassAuthorizeUrlResponseDto,
  UaePassCallbackResponseDto,
  UaePassRegisterPayloadDto,
  UaePassRegisterResponseDto,
  LogoutResponseDto,
  UaePassDisableResponseDto,
  LoginMethodsResponseDto,
} from './authContracts'
import type {
  LoginInput,
  VerifyOtpInput,
  ResendOtpInput,
  UaePassCallbackInput,
  UaePassRegisterInput,
  LogoutResponse,
  UaePassDisableResponse,
  LoginMethodsResponse,
} from '../types/authTypes'

export async function login(input: LoginInput): Promise<LoginResponseDto> {
  const payload: LoginPayloadDto = {
    portalCode: input.portalCode,
    email: input.email,
  }
  const { data } = await authApi.post<LoginResponseDto>(AUTH_ENDPOINTS.LOGIN, payload)
  return data
}

export async function verifyOtp(input: VerifyOtpInput): Promise<VerifyOtpResponseDto> {
  const payload: VerifyOtpPayloadDto = {
    verificationId: input.verificationId,
    otp: input.otp,
  }
  const { data } = await authApi.post<VerifyOtpResponseDto>(AUTH_ENDPOINTS.VERIFY_OTP, payload)
  return data
}

export async function resendOtp(input: ResendOtpInput): Promise<ResendOtpResponseDto> {
  const payload: ResendOtpPayloadDto = {
    verificationId: input.verificationId,
  }
  const { data } = await authApi.post<ResendOtpResponseDto>(AUTH_ENDPOINTS.RESEND_OTP, payload)
  return data
}

// Asks the auth-service to build the UAE Pass authorization URL. The backend
// owns the OAuth client id / redirect uri / scope and generates the anti-CSRF
// `state` (stored server-side), so the portal only redirects to what it gets
// back — it never constructs the URL or holds UAE Pass secrets.
export async function getUaePassAuthorizeUrl(
  portalCode: string,
): Promise<UaePassAuthorizeUrlResponseDto> {
  const { data } = await authApi.get<UaePassAuthorizeUrlResponseDto>(
    AUTH_ENDPOINTS.UAE_PASS_AUTHORIZE_URL,
    { params: { portalCode } },
  )
  return data
}

// Completes UAE Pass login: hands the authorization code + state back to the
// auth-service, which validates state, exchanges the code, resolves/provisions
// the user, and returns our own access + refresh tokens.
export async function exchangeUaePassCode(
  input: UaePassCallbackInput,
): Promise<UaePassCallbackResponseDto> {
  const payload: UaePassCallbackPayloadDto = {
    code: input.code,
    state: input.state,
  }
  const { data } = await authApi.post<UaePassCallbackResponseDto>(
    AUTH_ENDPOINTS.UAE_PASS_CALLBACK,
    payload,
  )
  return data
}

// Completes UAE Pass sign-up: redeems the single-use signup ticket (minted by
// the callback) plus the mobile the user confirmed, and the auth-service
// provisions the account with both OTP + UAE Pass enabled and returns tokens.
export async function registerWithUaePass(
  input: UaePassRegisterInput,
): Promise<UaePassRegisterResponseDto> {
  const payload: UaePassRegisterPayloadDto = {
    signupTicket: input.signupTicket,
    mobileCountryCode: input.mobileCountryCode,
    mobile: input.mobile,
  }
  const { data } = await authApi.post<UaePassRegisterResponseDto>(
    AUTH_ENDPOINTS.UAE_PASS_REGISTER,
    payload,
  )
  return data
}

// Enable UAE Pass from Account Settings — asks the auth-service to build an
// authorize URL bound to a link intent. This call is authenticated (authApi
// attaches the Bearer token); the backend reads the current user from the JWT
// and binds it into the OAuth `state`, so the callback links to exactly this
// user. No portalCode is sent — it comes from the token server-side.
export async function getUaePassLinkAuthorizeUrl(): Promise<UaePassAuthorizeUrlResponseDto> {
  const { data } = await authApi.get<UaePassAuthorizeUrlResponseDto>(
    AUTH_ENDPOINTS.UAE_PASS_LINK_AUTHORIZE_URL,
  )
  return data
}

// Disable UAE Pass as a login method on the current account. The auth-service
// soft-disables the method (reversible by re-enabling) and refuses if it is the
// user's only enabled login method.
export async function disableUaePass(): Promise<UaePassDisableResponse> {
  const { data } = await authApi.delete<UaePassDisableResponseDto>(AUTH_ENDPOINTS.UAE_PASS_LINK)
  return parseUaePassDisableResponse(data)
}

// Authoritative read of which login methods are enabled on the current account.
// Account Settings renders its toggles from this — the enabled state must never
// be inferred from local state, which drifts across devices and is lost on
// logout.
export async function getLoginMethods(): Promise<LoginMethodsResponse> {
  const { data } = await authApi.get<LoginMethodsResponseDto>(AUTH_ENDPOINTS.LOGIN_METHODS)
  return parseLoginMethodsResponse(data)
}

// Server-side session invalidation — the auth-service revokes the
// access + refresh tokens. The refresh token goes in the body per the
// LogoutDto contract (auth-service uses it to find the session row).
// Local state must still be cleared even if this call fails (network,
// server down, token already expired) so the user is never stuck in a
// signed-in UI; the caller (AppLayout.handleLogout) wraps this in
// try/finally to guarantee that.
export async function logout(refreshToken: string): Promise<LogoutResponse> {
  const { data } = await authApi.post<LogoutResponseDto>(AUTH_ENDPOINTS.LOGOUT, { refreshToken })
  return parseLogoutResponse(data)
}

export async function getPrivacyPolicy(): Promise<PrivacyPolicyResponseDto> {
  const { data } = await authApi.get<PrivacyPolicyResponseDto>(AUTH_ENDPOINTS.PRIVACY_POLICY)
  return data
}

export async function getTermsAndConditions(): Promise<TermsAndConditionsResponseDto> {
  const { data } = await authApi.get<TermsAndConditionsResponseDto>(
    AUTH_ENDPOINTS.TERMS_AND_CONDITIONS,
  )
  return data
}

export async function getInvestorRiskPolicy(): Promise<InvestorRiskPolicyResponseDto> {
  const { data } = await authApi.get<InvestorRiskPolicyResponseDto>(
    AUTH_ENDPOINTS.INVESTOR_RISK_POLICY,
  )
  return data
}
